import React, { useEffect, useState } from 'react';
import comissaoService from '../services/comissaoService';
import type { Comissao, ComissaoMembro } from '../services/comissaoService';
import userAdminService from '../services/userAdminService';
import type { UserAdminDTO } from '../services/userAdminService';
import Toast from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { Permission } from '../types/user.types';
import { UI_THEME } from '../ui/theme';
import { toUpperText } from '../utils/formatters';

const MAX_COMISSOES = 2;

export default function ComissaoAdminPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(Permission.MANAGE_COMISSOES);
  const [comissoes, setComissoes] = useState<Comissao[]>([]);
  const [usuarios, setUsuarios] = useState<UserAdminDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ numero: '', numeroPortaria: '', anoPortaria: '' });
  const [saving, setSaving] = useState(false);
  const [membroCpf, setMembroCpf] = useState<Record<string, string>>({});
  const [addingMembro, setAddingMembro] = useState<string | null>(null);
  const [designandoPresidente, setDesignandoPresidente] = useState<string | null>(null);
  const [substituindoMembroId, setSubstituindoMembroId] = useState<string | null>(null);
  const [suplenteCpf, setSuplenteCpf] = useState<Record<string, string>>({});
  const [savingSubstituicao, setSavingSubstituicao] = useState<string | null>(null);

  const isSaas = UI_THEME === 'saas';
  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const subtleSurfaceClass = isSaas
    ? 'border-teal-100 bg-teal-50/60 dark:border-teal-900/60 dark:bg-teal-950/30'
    : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const bodyTextClass = 'text-slate-600 dark:text-slate-300';
  const labelTextClass = 'text-slate-500 dark:text-slate-400';
  const strongTextClass = 'text-slate-900 dark:text-slate-100';
  const inputClass = 'w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40';
  const primaryButtonClass = isSaas
    ? 'bg-teal-600 text-white hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-400'
    : 'bg-blue-700 text-white hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500';
  const secondaryButtonClass = 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';

  function onlyDigits(value: string) {
    return value.replace(/\D/g, '');
  }

  function formatCPF(value: string) {
    const digits = onlyDigits(value).slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const [cs, us] = await Promise.all([
        comissaoService.listar(),
        userAdminService.list(),
      ]);
      setComissoes(cs);
      setUsuarios(us.filter(u => u.role !== 'OPERADOR' && u.status === 'ATIVO'));
    } catch {
      setToast({ message: 'Erro ao carregar dados', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  async function handleCriar(e: React.FormEvent) {
    e.preventDefault();
    if (!form.numero || !form.numeroPortaria || !form.anoPortaria) {
      setToast({ message: 'Preencha todos os campos', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      await comissaoService.criar({
        numero: Number(form.numero),
        numeroPortaria: form.numeroPortaria,
        anoPortaria: Number(form.anoPortaria),
      });
      setForm({ numero: '', numeroPortaria: '', anoPortaria: '' });
      setShowForm(false);
      setToast({ message: 'Comissão criada com sucesso', type: 'success' });
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { erro?: string } } })?.response?.data?.erro || 'Erro ao criar comissão';
      setToast({ message: msg, type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleAdicionarMembro(comissaoId: string) {
    const usuario = usuarioEncontrado(comissaoId);
    if (!usuario) {
      setToast({ message: 'Digite um CPF válido de um membro cadastrado', type: 'error' });
      return;
    }
    setAddingMembro(comissaoId);
    try {
      await comissaoService.adicionarMembro(comissaoId, Number(usuario.id));
      setMembroCpf(prev => ({ ...prev, [comissaoId]: '' }));
      setToast({ message: 'Membro vinculado com sucesso', type: 'success' });
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { erro?: string } } })?.response?.data?.erro || 'Erro ao adicionar membro';
      setToast({ message: msg, type: 'error' });
    } finally {
      setAddingMembro(null);
    }
  }

  async function handleInativarMembro(comissaoId: string, membroId: string) {
    if (!confirm('Inativar este membro da comissão? Ele deixará de participar das novas votações, mas o histórico será preservado.')) return;
    try {
      await comissaoService.inativarMembro(comissaoId, membroId);
      setToast({ message: 'Membro inativado', type: 'success' });
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { erro?: string } } })?.response?.data?.erro || 'Erro ao inativar membro';
      setToast({ message: msg, type: 'error' });
    }
  }

  function usuarioEncontradoParaSuplente(comissaoId: string, membroId: string): UserAdminDTO | undefined {
    const cpf = onlyDigits(suplenteCpf[membroId] || '');
    if (cpf.length !== 11) return undefined;
    return usuariosDisponiveis(comissaoId).find(u => onlyDigits(u.documento || '') === cpf);
  }

  async function handleSubstituirMembro(comissao: Comissao, membro: ComissaoMembro) {
    const suplente = usuarioEncontradoParaSuplente(comissao.id, membro.id);
    if (!suplente) {
      setToast({ message: 'Digite um CPF válido de um membro cadastrado', type: 'error' });
      return;
    }
    if (!confirm(`Substituir ${membro.nomeCompleto || 'este membro'} por ${suplente.nomeCompleto}? O parecer que ${membro.nomeCompleto || 'o titular'} já tiver registrado continua valendo; o suplente só precisa votar nos planos ainda pendentes.`)) return;
    setSavingSubstituicao(membro.id);
    try {
      await comissaoService.substituirMembro(comissao.id, membro.id, Number(suplente.id));
      setSuplenteCpf(prev => ({ ...prev, [membro.id]: '' }));
      setSubstituindoMembroId(null);
      setToast({ message: 'Suplente vinculado com sucesso', type: 'success' });
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { erro?: string } } })?.response?.data?.erro || 'Erro ao substituir membro';
      setToast({ message: msg, type: 'error' });
    } finally {
      setSavingSubstituicao(null);
    }
  }

  async function handleDesignarPresidente(comissao: Comissao, membro: ComissaoMembro) {
    if (membro.isPresidente) {
      if (!confirm('Remover a designação de presidente deste membro?')) return;
      setDesignandoPresidente(membro.id);
      try {
        const membros = await comissaoService.removerPresidente(comissao.id);
        setComissoes(current => current.map(c => c.id === comissao.id ? { ...c, membros } : c));
        setToast({ message: 'Designação de presidente removida', type: 'success' });
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { erro?: string } } })?.response?.data?.erro || 'Erro ao remover presidente';
        setToast({ message: msg, type: 'error' });
      } finally {
        setDesignandoPresidente(null);
      }
    } else {
      if (!confirm(`Designar ${membro.nomeCompleto || 'este membro'} como presidente da comissão?`)) return;
      setDesignandoPresidente(membro.id);
      try {
        const membros = await comissaoService.designarPresidente(comissao.id, membro.id);
        setComissoes(current => current.map(c => c.id === comissao.id ? { ...c, membros } : c));
        setToast({ message: 'Presidente designado com sucesso', type: 'success' });
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { erro?: string } } })?.response?.data?.erro || 'Erro ao designar presidente';
        setToast({ message: msg, type: 'error' });
      } finally {
        setDesignandoPresidente(null);
      }
    }
  }

  function usuariosDisponiveis(comissaoId: string): UserAdminDTO[] {
    const membroIds = new Set(
      comissoes
        .find(c => c.id === comissaoId)
        ?.membros
        .filter(m => m.ativo)
        .map(m => Number(m.usuarioId)) || []
    );
    return usuarios.filter(u => !membroIds.has(u.id));
  }

  function usuarioEncontrado(comissaoId: string): UserAdminDTO | undefined {
    const cpf = onlyDigits(membroCpf[comissaoId] || '');
    if (cpf.length !== 11) return undefined;
    return usuariosDisponiveis(comissaoId).find(u => onlyDigits(u.documento || '') === cpf);
  }

  if (loading) {
    return <div className={`flex h-64 items-center justify-center ${labelTextClass}`}>Carregando...</div>;
  }

  const totalMembros = comissoes.reduce((sum, comissao) => sum + comissao.membros.length, 0);
  const totalAtivos = comissoes.reduce((sum, comissao) => sum + comissao.membros.filter(membro => membro.ativo).length, 0);
  const totalPresidentes = comissoes.reduce((sum, comissao) => sum + comissao.membros.filter(membro => membro.isPresidente).length, 0);
  const summaryCards = [
    { label: 'Comissões', value: comissoes.length, dot: isSaas ? 'bg-teal-500' : 'bg-blue-600' },
    { label: 'Membros', value: totalMembros, dot: 'bg-slate-400' },
    { label: 'Membros ativos', value: totalAtivos, dot: 'bg-emerald-500' },
    { label: 'Presidentes', value: totalPresidentes, dot: 'bg-amber-500' },
  ];

  return (
    <div className="w-full space-y-6">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <section className={`rounded-3xl border ${heroClass}`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>
              {isSaas ? 'Seleção' : 'Administrador'}
            </p>
            <h1 className={`mt-2 text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>Comissões de seleção</h1>
            <p className={`mt-2 text-sm leading-6 ${bodyTextClass}`}>Gerenciamento das comissões e seus membros.</p>
          </div>
          {comissoes.length < MAX_COMISSOES && (
            <button
              onClick={() => setShowForm(true)}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${primaryButtonClass}`}
            >
              Nova comissão
            </button>
          )}
        </div>

        <div className={`mt-5 overflow-hidden rounded-2xl border ${surfaceClass}`}>
          <div className="grid divide-y divide-slate-200 dark:divide-slate-700 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
            {summaryCards.map((card) => (
              <div key={card.label} className="min-h-[72px] px-4 py-3">
                <p className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${labelTextClass}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${card.dot}`} />
                  {card.label}
                </p>
                <p className={`mt-2 text-2xl font-bold ${strongTextClass}`}>{card.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {showForm && (
        <form onSubmit={handleCriar} className={`space-y-4 rounded-3xl border p-5 ${subtleSurfaceClass}`}>
          <h2 className={`text-sm font-semibold ${accentTextClass}`}>Nova comissão</h2>
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <label className={`mb-1 block text-xs font-medium ${labelTextClass}`}>Número</label>
              <input
                type="number"
                min={1}
                value={form.numero}
                onChange={e => setForm(f => ({ ...f, numero: e.target.value }))}
                className={inputClass}
                placeholder="1"
              />
            </div>
            <div>
              <label className={`mb-1 block text-xs font-medium ${labelTextClass}`}>Número da portaria</label>
              <input
                type="text"
                value={form.numeroPortaria}
                onChange={e => setForm(f => ({ ...f, numeroPortaria: toUpperText(e.target.value) }))}
                className={inputClass}
                placeholder="042/2024"
              />
            </div>
            <div>
              <label className={`mb-1 block text-xs font-medium ${labelTextClass}`}>Ano</label>
              <input
                type="number"
                min={2000}
                max={2100}
                value={form.anoPortaria}
                onChange={e => setForm(f => ({ ...f, anoPortaria: e.target.value }))}
                className={inputClass}
                placeholder="2024"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className={`rounded-xl px-4 py-2 text-sm font-medium transition ${secondaryButtonClass}`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition disabled:opacity-50 ${primaryButtonClass}`}
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      )}

      {comissoes.length === 0 && (
        <div className={`rounded-3xl border border-dashed p-8 text-center ${surfaceClass} ${labelTextClass}`}>
          Nenhuma comissão cadastrada ainda.
        </div>
      )}

      {comissoes.map(comissao => {
        const usuarioLocalizado = usuarioEncontrado(comissao.id);
        const membrosAtivos = comissao.membros.filter(membro => membro.ativo).length;
        const membrosInativos = comissao.membros.length - membrosAtivos;

        return (
          <section key={comissao.id} className={`overflow-hidden rounded-3xl border ${surfaceClass}`}>
            <div className="flex items-start justify-between border-b border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-700 dark:bg-slate-800/80">
              <div>
                <h2 className={`text-base font-semibold ${strongTextClass}`}>Comissão nº {comissao.numero}</h2>
                <p className={`mt-0.5 text-sm ${labelTextClass}`}>
                  Portaria nº {comissao.numeroPortaria}/{comissao.anoPortaria}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                  {membrosAtivos} membro{membrosAtivos === 1 ? '' : 's'} ativo{membrosAtivos === 1 ? '' : 's'}
                </span>
                {membrosInativos > 0 && (
                  <span className={`text-xs ${labelTextClass}`}>
                    {membrosInativos} inativo{membrosInativos === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-3 p-5">
              {comissao.membros.length === 0 && (
                <p className={`text-sm italic ${labelTextClass}`}>Nenhum membro adicionado ainda.</p>
              )}

              {comissao.membros.map(membro => {
                const titularSubstituido = membro.substituiMembroId
                  ? comissao.membros.find(m => m.id === membro.substituiMembroId)
                  : undefined;
                return (
                <div key={membro.id}>
                <div className={`flex items-center justify-between rounded-2xl px-4 py-3 ${membro.ativo ? 'bg-slate-50 dark:bg-slate-800/70' : 'bg-amber-50 dark:bg-amber-950/30'}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-medium ${strongTextClass}`}>{membro.nomeCompleto || `Usuário ${membro.usuarioId}`}</p>
                      {membro.isPresidente && (
                        <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-950/50 dark:text-yellow-200">
                          Presidente
                        </span>
                      )}
                    </div>
                    <p className={`text-xs ${labelTextClass}`}>{[membro.email, membro.cargoFuncao, membro.perfil].filter(Boolean).join(' - ')}</p>
                    {titularSubstituido && (
                      <p className={`mt-1 text-xs ${labelTextClass}`}>
                        Suplente de {titularSubstituido.nomeCompleto || `Usuário ${titularSubstituido.usuarioId}`}. Pareceres já registrados por ele continuam valendo.
                      </p>
                    )}
                    {membro.possuiPareceres && (
                      <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                        Membro com histórico de pareceres preservado.
                      </p>
                    )}
                    {!membro.ativo && (
                      <p className={`mt-1 text-xs ${labelTextClass}`}>
                        Membro inativo. Não participa de novas composições ou votações.
                      </p>
                    )}
                  </div>
                  {membro.ativo ? (
                    <div className="flex items-center gap-2">
                      {membro.possuiPareceres && (
                        <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
                          Com histórico
                        </span>
                      )}
                      {canManage && (
                        <button
                          onClick={() => handleDesignarPresidente(comissao, membro)}
                          disabled={designandoPresidente === membro.id}
                          className={`text-xs font-medium ${membro.isPresidente ? 'text-yellow-700 hover:text-yellow-900 dark:text-yellow-300' : `${labelTextClass} hover:text-slate-700 dark:hover:text-slate-200`} disabled:opacity-50`}
                        >
                          {designandoPresidente === membro.id ? '...' : membro.isPresidente ? 'Remover presidente' : 'Presidir'}
                        </button>
                      )}
                      {membro.podeInativar ? (
                        <>
                          <button
                            onClick={() => setSubstituindoMembroId(prev => prev === membro.id ? null : membro.id)}
                            className={`text-xs font-medium ${labelTextClass} hover:text-slate-700 dark:hover:text-slate-200`}
                          >
                            Substituir por suplente
                          </button>
                          <button
                            onClick={() => handleInativarMembro(comissao.id, membro.id)}
                            className="text-xs font-medium text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-200"
                          >
                            Inativar
                          </button>
                        </>
                      ) : (
                        <div className="text-right">
                          <span className="rounded-full bg-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                            Inativação bloqueada
                          </span>
                          {membro.motivoBloqueioInativacao && (
                            <p className={`mt-1 max-w-52 text-xs ${labelTextClass}`}>
                              {membro.motivoBloqueioInativacao}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="rounded-full bg-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                      Inativo
                    </span>
                  )}
                </div>
                {substituindoMembroId === membro.id && (
                  <div className="mt-2 space-y-2 rounded-2xl border border-dashed border-slate-300 p-3 dark:border-slate-600">
                    <p className={`text-xs ${labelTextClass}`}>
                      Suplente que assume a vaga de {membro.nomeCompleto || `Usuário ${membro.usuarioId}`}. O parecer que ele já tiver registrado continua valendo para os planos já votados.
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={formatCPF(suplenteCpf[membro.id] || '')}
                        onChange={e => setSuplenteCpf(prev => ({ ...prev, [membro.id]: onlyDigits(e.target.value) }))}
                        className={`flex-1 ${inputClass}`}
                        placeholder="Digite o CPF do suplente..."
                      />
                      <button
                        onClick={() => handleSubstituirMembro(comissao, membro)}
                        disabled={!usuarioEncontradoParaSuplente(comissao.id, membro.id) || savingSubstituicao === membro.id}
                        className={`rounded-xl px-4 py-2 text-sm font-semibold transition disabled:opacity-50 ${primaryButtonClass}`}
                      >
                        {savingSubstituicao === membro.id ? '...' : 'Confirmar'}
                      </button>
                    </div>
                    {usuarioEncontradoParaSuplente(comissao.id, membro.id) && (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 dark:border-emerald-900/60 dark:bg-emerald-950/30">
                        <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                          {usuarioEncontradoParaSuplente(comissao.id, membro.id)?.nomeCompleto}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                </div>
                );
              })}

              <div className="space-y-2 pt-1">
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatCPF(membroCpf[comissao.id] || '')}
                    onChange={e => setMembroCpf(prev => ({ ...prev, [comissao.id]: onlyDigits(e.target.value) }))}
                    className={`flex-1 ${inputClass}`}
                    placeholder="Digite o CPF da pessoa da comissão..."
                  />
                  <button
                    onClick={() => handleAdicionarMembro(comissao.id)}
                    disabled={!usuarioLocalizado || addingMembro === comissao.id}
                    className={`rounded-xl px-4 py-2 text-sm font-semibold transition disabled:opacity-50 ${primaryButtonClass}`}
                  >
                    {addingMembro === comissao.id ? '...' : 'Adicionar'}
                  </button>
                </div>
                {membroCpf[comissao.id] && !usuarioLocalizado && onlyDigits(membroCpf[comissao.id]).length === 11 && (
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    Nenhum usuário ativo elegível para a comissão foi encontrado para esse CPF.
                  </p>
                )}
                {usuarioLocalizado && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 dark:border-emerald-900/60 dark:bg-emerald-950/30">
                    <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">{usuarioLocalizado.nomeCompleto}</p>
                    <p className="text-xs text-emerald-700 dark:text-emerald-300">
                      {[formatCPF(usuarioLocalizado.documento || ''), usuarioLocalizado.email, usuarioLocalizado.cargoFuncao].filter(Boolean).join(' - ')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
